export interface AddressResult {
  formatted_address: string;
  postcode: string;
  line_1: string;
  line_2?: string;
  line_3?: string;
  town_or_city: string;
  county: string;
  country?: string;
}

export interface PostcodeResult {
  postcode: string;
  latitude: number;
  longitude: number;
  addresses: AddressResult[];
}

interface PostcodesIoAddress {
  postcode: string;
  line_1: string;
  line_2?: string;
  line_3?: string;
  post_town: string;
  county: string;
  district: string;
  ward: string;
}

interface GooglePlaceAutocompleteResult {
  description: string;
  place_id: string;
  structured_formatting: {
    main_text: string;
    secondary_text: string;
  };
}

const POSTCODES_IO_BASE = 'https://api.postcodes.io';
const addressCache = new Map<string, AddressResult[]>();
const CACHE_DURATION = 5 * 60 * 1000;

function formatAddress(parts: {
  line_1: string;
  line_2?: string;
  line_3?: string;
  town: string;
  county: string;
  postcode: string;
}): string {
  const addressParts = [
    parts.line_1,
    parts.line_2,
    parts.line_3,
    parts.town,
    parts.county,
    parts.postcode
  ].filter(Boolean);

  return addressParts.join(', ');
}

export async function lookupPostcode(postcode: string): Promise<AddressResult[]> {
  const cleanPostcode = postcode.trim().toUpperCase().replace(/\s+/g, '');

  if (cleanPostcode.length < 5) {
    throw new Error('Please enter a valid UK postcode');
  }

  const cacheKey = `postcode_${cleanPostcode}`;
  const cached = addressCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(
      `${POSTCODES_IO_BASE}/postcodes/${encodeURIComponent(cleanPostcode)}`
    );

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Postcode not found. Please check and try again.');
      }
      throw new Error('Unable to lookup postcode. Please try again.');
    }

    const data = await response.json();

    if (!data.result) {
      throw new Error('No address data found for this postcode');
    }

    const result = data.result;
    const addresses: AddressResult[] = [];

    if (result.admin_district || result.parish) {
      const address: AddressResult = {
        postcode: result.postcode,
        line_1: result.parish || result.admin_district || '',
        town_or_city: result.admin_district || result.parish || '',
        county: result.admin_county || result.region || '',
        country: result.country,
        formatted_address: formatAddress({
          line_1: result.parish || result.admin_district || '',
          town: result.admin_district || '',
          county: result.admin_county || result.region || '',
          postcode: result.postcode
        })
      };
      addresses.push(address);
    }

    addressCache.set(cacheKey, addresses);
    setTimeout(() => addressCache.delete(cacheKey), CACHE_DURATION);

    return addresses;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Failed to lookup address. Please enter manually.');
  }
}

export async function autocompleteAddress(query: string): Promise<AddressResult[]> {
  const trimmedQuery = query.trim();

  if (trimmedQuery.length < 3) {
    return [];
  }

  const postcodePattern = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
  if (postcodePattern.test(trimmedQuery)) {
    return lookupPostcode(trimmedQuery);
  }

  const cacheKey = `autocomplete_${trimmedQuery.toLowerCase()}`;
  const cached = addressCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(
      `${POSTCODES_IO_BASE}/postcodes/${encodeURIComponent(trimmedQuery)}/autocomplete`
    );

    if (!response.ok) {
      return [];
    }

    const data = await response.json();

    if (!data.result || data.result.length === 0) {
      return [];
    }

    const results = await Promise.all(
      data.result.slice(0, 5).map(async (postcode: string) => {
        try {
          return await lookupPostcode(postcode);
        } catch {
          return [];
        }
      })
    );

    const addresses = results.flat();

    addressCache.set(cacheKey, addresses);
    setTimeout(() => addressCache.delete(cacheKey), CACHE_DURATION);

    return addresses;
  } catch (error) {
    console.error('Autocomplete error:', error);
    return [];
  }
}

export async function searchAddressWithGoogle(
  query: string,
  apiKey?: string
): Promise<AddressResult[]> {
  if (!apiKey) {
    console.warn('Google Places API key not configured');
    return [];
  }

  const trimmedQuery = query.trim();
  if (trimmedQuery.length < 3) {
    return [];
  }

  const cacheKey = `google_${trimmedQuery.toLowerCase()}`;
  const cached = addressCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(
      `https://maps.googleapis.com/maps/api/place/autocomplete/json?input=${encodeURIComponent(
        trimmedQuery
      )}&components=country:gb&key=${apiKey}`
    );

    if (!response.ok) {
      throw new Error('Google Places API request failed');
    }

    const data = await response.json();

    if (data.status !== 'OK' && data.status !== 'ZERO_RESULTS') {
      console.error('Google Places API error:', data.status);
      return [];
    }

    if (!data.predictions || data.predictions.length === 0) {
      return [];
    }

    const addresses: AddressResult[] = data.predictions.map(
      (prediction: GooglePlaceAutocompleteResult) => {
        const parts = prediction.description.split(', ');
        const postcode = parts[parts.length - 1];
        const county = parts.length > 2 ? parts[parts.length - 2] : '';
        const town = parts.length > 3 ? parts[parts.length - 3] : parts.length > 2 ? parts[parts.length - 2] : '';
        const line1 = parts[0] || '';
        const line2 = parts.length > 4 ? parts[1] : undefined;

        return {
          formatted_address: prediction.description,
          postcode: postcode,
          line_1: line1,
          line_2: line2,
          town_or_city: town,
          county: county,
          country: 'United Kingdom'
        };
      }
    );

    addressCache.set(cacheKey, addresses);
    setTimeout(() => addressCache.delete(cacheKey), CACHE_DURATION);

    return addresses;
  } catch (error) {
    console.error('Google Places error:', error);
    return [];
  }
}

export async function hybridAddressSearch(
  query: string,
  googleApiKey?: string
): Promise<AddressResult[]> {
  const trimmedQuery = query.trim();

  if (trimmedQuery.length < 3) {
    return [];
  }

  const postcodePattern = /[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}/i;
  const hasPostcode = postcodePattern.test(trimmedQuery);

  if (hasPostcode) {
    const match = trimmedQuery.match(postcodePattern);
    if (match) {
      try {
        return await lookupPostcode(match[0]);
      } catch (error) {
        console.error('Postcode lookup failed:', error);
      }
    }
  }

  if (googleApiKey) {
    try {
      const googleResults = await searchAddressWithGoogle(trimmedQuery, googleApiKey);
      if (googleResults.length > 0) {
        return googleResults;
      }
    } catch (error) {
      console.error('Google search failed, falling back:', error);
    }
  }

  try {
    return await autocompleteAddress(trimmedQuery);
  } catch (error) {
    console.error('Autocomplete failed:', error);
    return [];
  }
}

export function validateUKPostcode(postcode: string): boolean {
  const postcodePattern = /^[A-Z]{1,2}\d{1,2}[A-Z]?\s?\d[A-Z]{2}$/i;
  return postcodePattern.test(postcode.trim());
}

export function formatPostcode(postcode: string): string {
  const clean = postcode.trim().toUpperCase().replace(/\s+/g, '');

  if (clean.length < 5) return postcode;

  const outward = clean.slice(0, -3);
  const inward = clean.slice(-3);

  return `${outward} ${inward}`;
}
